/* ──────────────────────────────────────────────────────────────
   Calendario: ver, crear y editar reservas
   Colores conmutables (según rol):
     - PROFESOR: siempre "Por proyecto"
     - STAFF/otros: selector "Por estado" | "Por proyecto"
   DB-first:
     - Estado:   estadoColor (BBDD) → fallback catálogo getEstadoColor
     - Proyecto: proyecto.calendarColor (BBDD) → fallback color por estado
   ────────────────────────────────────────────────────────────── */

import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormatos } from '../../../hooks/useFormatos';
import { useAusencias } from '../../../hooks/useAusencias';
import { useVacaciones } from '../../../hooks/useVacaciones';
import FullCalendar from '@fullcalendar/react';
import {
  DateSelectArg,
  DatesSetArg,
  EventClickArg,
  EventContentArg,
  EventMountArg,
} from '@fullcalendar/core';
import { DateClickArg } from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import { ESTADOS, CURSOS } from '../../../constants/enums';
import { getEstadoColor } from '../../../constants/colors';
import { useProyectos } from '../../../hooks/useProyectos';
import {
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  CircularProgress,
  Typography,
  Popover,
  Autocomplete,
  TextField,
  MenuItem,
  Button,
  Box,
  Fab,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { PictureAsPdf } from '@mui/icons-material';
import { ChevronLeft, ChevronRight, DateRange, Add as AddIcon } from '@mui/icons-material';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { useReservas } from '../../../hooks/useReservas';
import type { ReservaFilters } from '../../../types/ReservaFilters';
import { useAuth } from '../../../context/AuthContext';

import { useProfesores } from '../../../hooks/useProfesores';
import { useClientes } from '../../../hooks/useClientes';
import { useAsignaturas } from '../../../hooks/useAsignaturas';
import { useCentros } from '../../../hooks/useCentros';
import { useUsuarios } from '../../../hooks/useUsuarios';

import ReservationFormDialog from '../../../components/ReservationFormDialog';
import ReservaDialog from '../../../components/ReservaDialog';
import { useTranslation } from 'react-i18next';

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/* ✨ NUEVO */
import { useDisponibilidad } from '../../../hooks/useDisponibilidad';
import api from '../../../api/axios';

/* ✨ NUEVO: Leyenda */
import CalendarLegend, { type LegendItem } from '../../../components/CalendarLegend';

/* ✨ NUEVO (2.3): hook de aulas por centro */
import { useAulasByCentro } from '../../../api/aulas'; // ⬅️ ajusta la ruta si tu hook está en otro lugar

dayjs.extend(isoWeek);

/* ───────── helper: normaliza color (HEX, rgb/hsl, var(), nombre CSS) ───────── */
function normalizeColor(c?: string | null): string | undefined {
  if (!c) return undefined;
  const s = String(c).trim();
  if (!s) return undefined;

  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(s)) return s;
  if (/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(s)) return `#${s}`;
  if (/^(rgb|rgba|hsl|hsla)\(/i.test(s)) return s;
  if (/^var\(--/.test(s)) return s;
  if (/^[a-z]+$/i.test(s)) return s;

  return undefined;
}

/* ───────── helper: contraste texto sobre color de fondo ───────── */
function getContrastYIQ(hexOrRgb: string) {
  let r: number, g: number, b: number;
  if (hexOrRgb?.startsWith?.('rgb')) {
    const rgb = hexOrRgb.match(/\d+/g);
    r = parseInt(rgb?.[0] ?? '0', 10);
    g = parseInt(rgb?.[1] ?? '0', 10);
    b = parseInt(rgb?.[2] ?? '0', 10);
  } else {
    const hc = (hexOrRgb || '#000000').replace('#', '');
    const h = hc.length >= 6 ? hc : '000000';
    r = parseInt(h.substring(0, 2), 16) || 0;
    g = parseInt(h.substring(2, 4), 16) || 0;
    b = parseInt(h.substring(4, 6), 16) || 0;
  }
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 155 ? 'black' : 'white';
}

/* ─────────── rango “histórico” para cargar alumnos del profe ─────────── */
const START_ALL = '2000-01-01';
const END_ALL = '2100-01-01';
const AULA_ESTADOS = ['NO_REQUIERE', 'PENDIENTE', 'ASIGNADA'] as const;

/* ═════════ props opcionales ═════════ */
interface Props {
  fixedFilters?: Partial<ReservaFilters>;
  canCreate?: boolean;
  canEdit?: boolean;
  onAmpliar?: (r: any) => void;
  showInactiveUsers?: boolean;
  onShowInactiveUsersChange?: (value: boolean) => void; // ✨ NUEVO: callback para comunicar cambios
}

/* ---------- helper título ---------- */
function headerTxt(view: string, start: dayjs.Dayjs, end: dayjs.Dayjs, t: (k: string) => string) {
  if (view === 'dayGridMonth') return start.add(15, 'day').format('MMMM YYYY');
  if (view === 'timeGridWeek')
    return `${t('calendar.week')} ${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
  return start.format('dddd D MMM YYYY');
}

/* ── Cancelado: por code (CANCEL_*) o por label (“Cancelled …”) ── */
const isCancelledEvt = (r: any) => {
  const code = String(r?.estadoCode ?? '');
  const label = String(r?.estado ?? '');
  return code.toUpperCase().startsWith('CANCEL') || label.toLowerCase().startsWith('cancel');
};

/* ✨ NUEVO: helper para detectar “Pendiente de reagendar” */
const isPendienteReagendarEvt = (r: any) => {
  const norm = (s: any) =>
    String(s ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '')
      .toLowerCase();

  const code = norm((r as any).estadoCode);
  const label = norm((r as any).estado);

  const hayReagendar =
    code.includes('reagend') ||
    code.includes('reschedul') ||
    label.includes('reagend') ||
    label.includes('reschedul');

  const hayPendiente = code.includes('pend') || code.includes('pending') || label.includes('pend');

  return hayReagendar && hayPendiente;
};

/* ── helper: dedup por id (seguro ante duplicados de API o cache) ── */
function uniqueById<T>(
  list: T[],
  getId: (item: T) => string = (item: any) => String(item?.id ?? ''),
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of list) {
    const id = getId(item);
    if (!id) {
      out.push(item);
      continue;
    }
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(item);
  }
  return out;
}

/* ─────────── NUEVO: modo de color ─────────── */
type ColorMode = 'estado' | 'proyecto';

export default function ReservasCalendarPage({
  fixedFilters = {},
  canCreate = true,
  canEdit = true,
  showInactiveUsers = false,
  onShowInactiveUsersChange,
}: Props) {
  /* ═════════ i18n ═════════ */
  const { t, i18n } = useTranslation<'translation'>();

  useEffect(() => {
    dayjs.locale(i18n.language);
  }, [i18n.language]);

  /* ═════════ tema ═════════ */
  const theme = useTheme();
  // Color personalizado para bloqueos y no disponibilidad
  const blockedBg = useMemo(() => alpha('#ff9142ff', 0.7), []);

  /* ═════════ rango + vista ═════════ */
  const [range, setRange] = useState<{
    start: dayjs.Dayjs | null;
    end: dayjs.Dayjs | null;
  }>({
    start: null,
    end: null,
  });
  const [view, setView] = useState<'dayGridMonth' | 'timeGridWeek' | 'dayGridDay'>(
    'dayGridMonth' as any,
  ); // mantenemos compatibilidad con tipos

  /* ═════════ filtros “visibles” ═════════ */
  const navigate = useNavigate();
  const location = useLocation();
  const [filters, setFilters] = useState<ReservaFilters>({});
  const setFilter = <K extends keyof ReservaFilters>(k: K, v: ReservaFilters[K]) =>
    setFilters((p) => ({ ...p, [k]: v == null ? undefined : v }));
  const clearFilters = () => {
    setFilters({});
    // ✨ limpiar también el nuevo filtro por ID
    setReservaIdFilter(null);
    setReservaIdQuery('');
    setSearchHit(null);
  };

  /* ✨ NUEVO: estado para BUSCADOR por ID */
  const [reservaIdQuery, setReservaIdQuery] = useState<string>('');
  const [reservaIdFilter, setReservaIdFilter] = useState<number | null>(null);
  const [searchHit, setSearchHit] = useState<any | null>(null);

  const applyIdFilter = useCallback(() => {
    const n = parseInt(String(reservaIdQuery).trim(), 10);
    if (Number.isFinite(n) && n > 0) {
      setReservaIdFilter(n);
    } else {
      setReservaIdFilter(null);
      setSearchHit(null);
    }
  }, [reservaIdQuery]);

  const clearIdFilter = useCallback(() => {
    setReservaIdFilter(null);
    setReservaIdQuery('');
    setSearchHit(null);
  }, []);

  /* ═════════ estado de canceladas ═════════ */
  const [showCancelled, setShowCancelled] = useState(false);

  /* ═════════ catálogos ═════════ */
  const { user } = useAuth();
  const roles: string[] = (user?.roles ?? []).map(String);
  const isProfesorLogueado = roles.includes('profesor');
  /* ✨ NUEVO (2.3): determinar staff (no profesor, no cliente) */
  const isCliente = roles.includes('cliente');
  const isStaff = !isProfesorLogueado && !isCliente;

  // ✨ MODIFICADO: usar useUsuarios con filtro correcto
  const { rows: profesoresData = [] } = useUsuarios({
    rol: 'profesor',
    soloActivos: !showInactiveUsers,
  });
  const profesores = useMemo(() => profesoresData, [profesoresData]);

  const { rows: clientesData = [] } = useUsuarios({
    rol: 'cliente',
    soloActivos: !showInactiveUsers,
  });
  const clientes = useMemo(() => clientesData, [clientesData]);

  const { rows: asignaturas = [] } = useAsignaturas();
  const { rows: centros = [] } = useCentros();
  const { rows: formatos = [] } = useFormatos();
  const { rows: proyectos = [] } = useProyectos();

  /* Mapa de proyectos por id para lookup de color */
  const proyectosById = useMemo(() => {
    const m = new Map<number, any>();
    for (const p of proyectos ?? []) {
      const idNum = Number((p as any).id);
      if (Number.isFinite(idNum)) m.set(idNum, p);
    }
    return m;
  }, [proyectos]);

  /* ─────────── estado: modo de color ─────────── */
  const [colorMode, setColorMode] = useState<ColorMode>(() => {
    if (isProfesorLogueado) return 'proyecto';
    try {
      return ((localStorage.getItem('calendar.colorMode') as ColorMode) || 'estado') as ColorMode;
    } catch {
      return 'estado';
    }
  });

  useEffect(() => {
    if (isProfesorLogueado) setColorMode('proyecto');
  }, [isProfesorLogueado]);

  useEffect(() => {
    if (!isProfesorLogueado) {
      try {
        localStorage.setItem('calendar.colorMode', colorMode);
      } catch {}
    }
  }, [colorMode, isProfesorLogueado]);

  /* profesor logueado → mapear user.id → profesor.id */
  const meProfesor = useMemo(
    () =>
      isProfesorLogueado
        ? profesores.find(
            (p: any) => p.userId === user?.id || p.idUsr === user?.id || p.idUsuario === user?.id,
          )
        : undefined,
    [isProfesorLogueado, profesores, user],
  );

  /* profesorId real (primero fijo por props, si no, el del profe logueado) */
  const profesorIdReal: number | undefined = fixedFilters.profesorId ?? meProfesor?.id;
  const profesorFiltroId: number | undefined =
    fixedFilters.profesorId ?? filters.profesorId ?? profesorIdReal;

  const { vacData, refresh: refreshVacaciones } = useVacaciones(profesorFiltroId ?? 0);

  const { rows: ausencias = [], refresh: refreshAusencias } = useAusencias(profesorFiltroId ?? 0);

  /* ✨ NUEVO: Rejilla semanal del profesor */
  const { rows: rejillaRows = [], refresh: refreshRejilla } = useDisponibilidad(
    profesorFiltroId ?? 0,
  );

  /* ✨ NUEVO: Especiales del profesor (por año visible) */
  const [especiales, setEspeciales] = useState<any[]>([]);

  useEffect(() => {
    if (profesorFiltroId) {
      refreshVacaciones();
      refreshAusencias();
      refreshRejilla();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profesorFiltroId]);

  /* Años que cubre el rango visible */
  const yearsInRange = useMemo(() => {
    if (!range.start || !range.end) return [] as number[];
    const ys = new Set<number>();
    let y = range.start.year();
    const last = range.end.year();
    while (y <= last) {
      ys.add(y);
      y++;
    }
    return Array.from(ys.values());
  }, [range.start, range.end]);

  /* Fetch de especiales al cambiar profe o los años visibles */
  useEffect(() => {
    let cancelled = false;
    const fetchEspeciales = async () => {
      if (!profesorFiltroId || yearsInRange.length === 0) {
        setEspeciales([]);
        return;
      }
      try {
        const results = await Promise.all(
          yearsInRange.map((yy) =>
            api.get(`/disponibilidad-especial?profesor=${profesorFiltroId}&year=${yy}`),
          ),
        );
        if (!cancelled) {
          const map = new Map<number, any>();
          for (const { data } of results) {
            for (const row of data ?? []) map.set(row.id, row);
          }
          setEspeciales(Array.from(map.values()));
        }
      } catch {
        if (!cancelled) setEspeciales([]);
      }
    };
    fetchEspeciales();
    return () => {
      cancelled = true;
    };
  }, [profesorFiltroId, yearsInRange]);

  /* Establecer filtro profesorId automáticamente si procede (solo si no viene fijo) */
  useEffect(() => {
    if (isProfesorLogueado && !fixedFilters.profesorId && profesorIdReal && !filters.profesorId) {
      setFilters((p) => ({ ...p, profesorId: profesorIdReal }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProfesorLogueado, profesorIdReal, fixedFilters.profesorId]);

  /* ═════════ reservas (rango visible) ═════════ */
  const { rows, loading } = useReservas(
    range.start ? range.start.format('YYYY-MM-DD') : null,
    range.end ? range.end.format('YYYY-MM-DD') : null,
    { ...filters, ...fixedFilters, incluirCanceladas: showCancelled },
  );

  /* —— alumnos del profesor usando TODO el histórico (solo cuando haya profesorIdReal) —— */
  const { rows: reservasHistProf = [] } = useReservas(
    profesorIdReal ? START_ALL : null,
    profesorIdReal ? END_ALL : null,
    profesorIdReal ? { profesorId: profesorIdReal } : {},
  );

  /* Construir lista de clientes visibles (según rol) */
  const clientesDelProfesor = useMemo(() => {
    if (!isProfesorLogueado) return clientes;

    const ids = new Set<number>();
    reservasHistProf.forEach((r: any) => {
      const titular = Number(r.idCliente);
      if (Number.isFinite(titular)) ids.add(titular);
      (r.alumnosIds ?? []).forEach((id: any) => {
        const n = Number(id);
        if (Number.isFinite(n)) ids.add(n);
      });
    });

    return clientes.filter((c: any) => ids.has(Number(c.id)));
  }, [isProfesorLogueado, reservasHistProf, clientes]);

  // si había un alumno seleccionado que ya no procede, lo limpiamos
  useEffect(() => {
    if (
      (filters as any).clienteIdUsr &&
      !clientesDelProfesor.some((c: any) => Number(c.id) === Number((filters as any).clienteIdUsr))
    ) {
      setFilter('clienteIdUsr', undefined as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientesDelProfesor]);

  /* ═══════ lógica llegada desde NotificationBell ═══════ */
  const [pendingId, setPendingId] = useState<number | null>(null);

  useEffect(() => {
    const id = (location.state as any)?.reservaId;
    if (id) {
      setPendingId(id);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const [selected, setSelected] = useState<any | null>(null);

  useEffect(() => {
    if (!pendingId) return;
    const hit = rows.find((r) => r.id === pendingId);
    if (hit && !(isProfesorLogueado && isPendienteReagendarEvt(hit))) {
      setSelected(hit);
      setPendingId(null);
    }
  }, [pendingId, rows, isProfesorLogueado]);

  /* ═══════ CRUD helpers ═══════ */
  const refetchEvents = () => calRef.current?.getApi().refetchEvents();

  /* ==== captura PDF ==== */
  const captureRef = useRef<HTMLDivElement>(null);

  /* —— diálogo Create / Edit —— */
  const [dlg, setDlg] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    initial?: any;
    id?: number;
  }>({ open: false, mode: 'create' });

  const openCreate = useCallback(
    (start: Date, end: Date) =>
      setDlg({
        open: true,
        mode: 'create',
        initial: {
          fecha: dayjs(start).format('YYYY-MM-DD'),
          horaInicio: dayjs(start).format('HH:mm'),
          horaFin: dayjs(end).format('HH:mm'),
        },
      }),
    [],
  );

  const openEdit = useCallback(
    (evt: any) =>
      setDlg({
        open: true,
        mode: 'edit',
        id: evt.id,
        initial: {
          idCliente: evt.idCliente ?? evt.id_cliente ?? evt.clienteId,
          idProfesor: evt.idProfesor ?? evt.id_profesor ?? evt.profesorId,
          nombreProf: evt.nombreProf ?? evt.nombreProfesor ?? evt.profesorNombre,
          idAsignatura: evt.idAsignatura ?? evt.id_asignatura ?? evt.asignaturaId,
          idFormato: evt.idFormato ?? evt.id_formato ?? evt.formatoId,
          idCentro: evt.idCentro ?? evt.id_centro ?? evt.centroId ?? undefined,
          fecha: evt.fecha,
          horaInicio: evt.horaInicio,
          horaFin: evt.horaFin,
          idProyecto: evt.idProyecto ?? evt.id_proyecto ?? evt.proyectoId,
          curso: evt.studentYear ?? undefined,
          examinationBoard: evt.examBoard ?? undefined,
          linkAddress: evt.link ?? undefined,
          language: evt.language,
          comentarios: evt.comentarios ?? undefined,
          comentariosInternos: evt.comentariosInternos ?? undefined,
          estado: evt.estado,
          alumnosIds: evt.alumnosIds,
          numAlumnos: evt.numAlumnos,
          idRecurrencia: evt.idRecurrencia || undefined,
          // Shadowing: incluir datos si vienen en el evento para que el formulario de edición los muestre
          idProfesorShadow:
            (evt as any).idProfesorShadow ?? (evt as any).id_profesor_shadow ?? undefined,
          shadowing: (evt as any).shadowing ?? undefined,
          shadowingInfo: (evt as any).shadowingInfo ?? (evt as any).shadowing ?? undefined,
        },
      }),
    [],
  );

  const openDuplicate = useCallback((evt: any) => {
    setDlg({
      open: true,
      mode: 'create',
      initial: {
        idCliente: evt.idCliente ?? evt.id_cliente ?? evt.clienteId,
        idProfesor: evt.idProfesor ?? evt.id_profesor ?? evt.profesorId,
        nombreProf: evt.nombreProf ?? evt.nombreProfesor ?? evt.profesorNombre,
        idAsignatura: evt.idAsignatura ?? evt.id_asignatura ?? evt.asignaturaId,
        idFormato: evt.idFormato ?? evt.id_formato ?? evt.formatoId,
        idCentro: evt.idCentro ?? evt.id_centro ?? evt.centroId ?? undefined,
        fecha: evt.fecha,
        horaInicio: evt.horaInicio,
        horaFin: evt.horaFin,
        idProyecto: evt.idProyecto ?? evt.id_proyecto ?? evt.proyectoId,
        curso: evt.studentYear ?? undefined,
        examinationBoard: evt.examBoard ?? undefined,
        linkAddress: evt.link ?? undefined,
        language: evt.language,
        comentarios: evt.comentarios ?? undefined,
        alumnosIds: evt.alumnosIds,
        numAlumnos: evt.numAlumnos,
        idProfesorShadow:
          evt.idProfesorShadow ?? evt.id_profesor_shadow ?? evt.shadowingInfo?.profesor?.id,
        shadowing: evt.shadowing ?? Boolean(evt.idProfesorShadow ?? evt.id_profesor_shadow),
      },
    });
  }, []);

  const goToNuevaReserva = useCallback(
    (evt: any) => {
      const initialValues = {
        idCliente: evt.idCliente,
        idProfesor: evt.idProfesor,
        idAsignatura: evt.idAsignatura,
        idFormato: evt.idFormato,
        idCentro: evt.idCentro ?? undefined,
        idProyecto: evt.idProyecto,
        language: evt.language,
        curso: evt.studentYear ?? undefined,
        examinationBoard: evt.examBoard ?? undefined,
        linkAddress: evt.link ?? undefined,
      };
      navigate('/reservas/nueva', { state: { initialValues } });
    },
    [navigate],
  );

  // ✨ ELIMINADO: ya no necesitamos estado local, usamos directamente la prop y el callback
  // const [localShowInactive, setLocalShowInactive] = useState(showInactiveUsers);
  // useEffect(() => { setLocalShowInactive(showInactiveUsers); }, [showInactiveUsers]);

  /* ✨ Helper para crear un evento de calendar a partir de una reserva */
  const buildReservaEvent = useCallback(
    (r: any) => {
      // Color por ESTADO (DB-first) → fallback catálogo
      const colorFromEstadoDb = normalizeColor((r as any).estadoColor);
      const colorFromEstadoFallback = getEstadoColor((r as any).estadoCode ?? (r as any).estado);
      const estadoColorFinal = colorFromEstadoDb ?? colorFromEstadoFallback;

      // Color por PROYECTO (DB-first) → fallback al color por estado
      let finalColor = estadoColorFinal;
      // Profesores: siempre color por proyecto, EXCEPTO cuando están viendo "canceladas"
      const mustUseProyecto =
        (colorMode === 'proyecto' || isProfesorLogueado) && !(isProfesorLogueado && showCancelled);

      if (mustUseProyecto) {
        const projId = Number((r as any).idProyecto);
        const proyecto = proyectosById.get(projId);
        const projColor = normalizeColor(proyecto?.calendarColor);
        finalColor = projColor ?? estadoColorFinal;
      }

      const esGrupal = (r as any).numAlumnos > 1;

      // Intentamos ser tolerantes con distintos formatos de fechas
      const startIso =
        (r as any).inicio ||
        ((r as any).fecha && (r as any).horaInicio
          ? `${(r as any).fecha}T${(r as any).horaInicio}:00`
          : undefined);
      const endIso =
        (r as any).fin ||
        ((r as any).fecha && (r as any).horaFin
          ? `${(r as any).fecha}T${(r as any).horaFin}:00`
          : undefined);

      const bgColor = finalColor;
      const textColor = getContrastYIQ(bgColor || '#21a598');

      return {
        id: String((r as any).id),
        start: startIso,
        end: endIso,
        title: ' ',
        backgroundColor: bgColor,
        borderColor: bgColor,
        textColor,
        extendedProps: {
          ...(r as any),
          nombreAlumno: (r as any).nombreCliente,
          asignatura: (r as any).nombreAsignatura,
          nombreProf: (r as any).nombreProf,
          esGrupal,
          incidencia: (r as any).incidencia,
        },
      } as any;
    },
    [colorMode, isProfesorLogueado, showCancelled, proyectosById],
  );

  /* ═══════ eventos FullCalendar (reservas) ═══════ */
  const reservaEvents = useMemo(() => {
    // Cuando esté activo el filtro por ID, ignoramos el estado cancelado/activo
    const filtered = rows.filter((r) => {
      // ✨ Ocultar “pendiente de reagendar” para profesores
      if (isProfesorLogueado && isPendienteReagendarEvt(r)) return false;

      if (reservaIdFilter != null) return Number((r as any).id) === reservaIdFilter;
      return showCancelled ? isCancelledEvt(r) : !isCancelledEvt(r);
    });

    const uniqueFiltered = uniqueById(filtered);
    const evs = uniqueFiltered.map(buildReservaEvent);

    // Si hay un hit directo por API y no está en el rango visible, lo añadimos
    if (
      reservaIdFilter != null &&
      searchHit &&
      String(searchHit.id) === String(reservaIdFilter) &&
      !evs.some((e) => String(e.id) === String(reservaIdFilter)) &&
      !(isProfesorLogueado && isPendienteReagendarEvt(searchHit)) // ✨ no añadir si es “pendiente de reagendar” para profesores
    ) {
      evs.push(buildReservaEvent(searchHit));
    }

    return evs;
  }, [rows, showCancelled, reservaIdFilter, searchHit, buildReservaEvent, isProfesorLogueado]);

  /* ═══════ overlays de bloqueos/no disponibilidad ═══════ */
  const diasNoDisponibles = useMemo(() => {
    if (!profesorFiltroId) return new Set<string>();
    const s = new Set<string>();
    for (const v of vacData) if (v?.fecha) s.add(v.fecha); // YYYY-MM-DD
    for (const a of ausencias) if (a?.estado === 'approved' && a?.fecha) s.add(a.fecha);
    return s;
  }, [profesorFiltroId, vacData, ausencias]);

  const bloqueoEvents = useMemo(() => {
    if (!profesorFiltroId || !range.start || !range.end) return [] as any[];

    const out: any[] = [];
    diasNoDisponibles.forEach((d) => {
      const dd = dayjs(d);
      const dentro = !dd.isBefore(range.start, 'day') && !dd.isAfter(range.end, 'day');
      if (!dentro) return;

      out.push({
        id: `ND-${d}`,
        start: d,
        end: dayjs(d).add(1, 'day').format('YYYY-MM-DD'),
        allDay: true,
        display: 'background',
        backgroundColor: blockedBg,
      });
    });

    return out;
  }, [profesorFiltroId, diasNoDisponibles, range.start, range.end, blockedBg]);

  /* ✨ NUEVO: helpers tooltip MES */
  const timeToMin = (hhmmOrHms: string) => {
    const [h, m] = hhmmOrHms
      .slice(0, 5)
      .split(':')
      .map((n) => parseInt(n, 10) || 0);
    return h * 60 + m;
  };
  const minToHHMM = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const p = (x: number) => String(x).padStart(2, '0');
    return `${p(h)}:${p(m)}`;
  };
  const fmtDisponibles = (list: { ini: number; fin: number }[]) =>
    list.length
      ? list.map((tr) => `${minToHHMM(tr.ini)}\u2011${minToHHMM(tr.fin)}`).join(', ')
      : '—';
  const getAusenciaReason = (dateStr: string) => {
    const delDia = ausencias.filter((a: any) => a?.estado === 'approved' && a?.fecha === dateStr);
    if (!delDia.length) return null;
    const motivos = Array.from(
      new Set(
        delDia
          .map((a: any) => a?.motivo?.nombreEs)
          .filter((x: any) => typeof x === 'string' && x.trim().length > 0),
      ),
    ) as string[];
    return motivos.length ? `ausencia (${motivos.join(', ')})` : 'ausencia';
  };
  const getVacacionesReason = (dateStr: string) => {
    const delDia = vacData.filter((v: any) => v?.fecha === dateStr);
    if (!delDia.length) return null;
    const tramos = Array.from(
      new Set(
        delDia
          .map((v: any) => v?.tramo)
          .filter((x: any) => typeof x === 'string' && x.trim().length > 0),
      ),
    ) as string[];
    if (!tramos.length || tramos.includes('completo')) return 'vacaciones';
    return `vacaciones (${tramos.join(', ')})`;
  };
  const buildDisponibilidadTooltip = (args: {
    dateStr: string;
    disponibles: { ini: number; fin: number }[];
    weeklyDisponibles: { ini: number; fin: number }[];
    espPositivos: any[];
    espBloqueos: any[];
  }) => {
    const { dateStr, disponibles, weeklyDisponibles, espPositivos, espBloqueos } = args;
    if (disponibles.length > 0) return `Disponible: ${fmtDisponibles(disponibles)}.`;

    const motivos: string[] = [];

    const ausenciaReason = getAusenciaReason(dateStr);
    if (ausenciaReason) motivos.push(ausenciaReason);

    const vacacionesReason = getVacacionesReason(dateStr);
    if (vacacionesReason) motivos.push(vacacionesReason);

    if (espBloqueos.length > 0) {
      const tramosBloqueados = mergeIntervals(
        espBloqueos.map((e) => ({
          ini: timeToMin(e.horaInicio),
          fin: timeToMin(e.horaFin),
        })),
      );
      motivos.push(`disponibilidad especial bloqueada (${fmtDisponibles(tramosBloqueados)})`);
    }

    if (weeklyDisponibles.length === 0 && espPositivos.length === 0) {
      motivos.push('sin disponibilidad normal configurada');
    }

    if (!motivos.length) {
      motivos.push('reglas de disponibilidad');
    }

    return `No disponible por: [${motivos.join(' | ')}].`;
  };
  const mergeIntervals = (list: { ini: number; fin: number }[]) => {
    if (!list.length) return [];
    const sorted = [...list].sort((a, b) => a.ini - b.ini);
    const out: { ini: number; fin: number }[] = [];
    let cur = { ...sorted[0] };
    for (let i = 1; i < sorted.length; i++) {
      const x = sorted[i];
      if (x.ini <= cur.fin) {
        cur.fin = Math.max(cur.fin, x.fin);
      } else {
        out.push(cur);
        cur = { ...x };
      }
    }
    out.push(cur);
    return out;
  };

  const dayjsToModeloDow = (d: dayjs.Dayjs) => (d.day() + 6) % 7; // L=0 … D=6

  const centroFiltroId: number | undefined =
    (fixedFilters.centroId as any) ?? (filters.centroId as any);
  const formatoFiltroId: number | undefined =
    (fixedFilters.idFormato as any) ?? (filters.idFormato as any);

  const formatoFiltroMask = useMemo(() => {
    if (formatoFiltroId === 1) return 2; // online
    if (formatoFiltroId === 2) return 1; // presencial
    if (formatoFiltroId === 3) return 4; // domicilio
    if (formatoFiltroId === 4) return 7; // todos
    return 0; // sin filtro de formato
  }, [formatoFiltroId]);

  const formatoEsOnline = formatoFiltroMask === 2;

  const cumpleFormato = useCallback(
    (mask?: number | null) =>
      formatoFiltroMask === 0 || (Number(mask ?? 7) & formatoFiltroMask) !== 0,
    [formatoFiltroMask],
  );

  const cumpleCentro = useCallback(
    (idCentro?: number | null) => {
      if (!centroFiltroId || formatoEsOnline) return true;
      return idCentro == null || Number(idCentro) === Number(centroFiltroId);
    },
    [centroFiltroId, formatoEsOnline],
  );

  /* ✨ NUEVO (2.3): aulas del centro seleccionado (solo para staff) */
  const { data: aulas = [] } = useAulasByCentro(centroFiltroId ?? 0);

  /* ✨ NUEVO (2.3): coherencia entre centro ↔ aula */
  useEffect(() => {
    // Si no hay centro seleccionado, limpiamos aulaId si estuviera puesto
    if (!centroFiltroId && (filters as any).aulaId != null) {
      setFilters((p) => ({ ...p, aulaId: undefined }) as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centroFiltroId]);

  useEffect(() => {
    // Si el aula seleccionada ya no pertenece al centro (o cambia la lista), limpiar aulaId
    if ((filters as any).aulaId != null) {
      const ok = (aulas as any[]).some(
        (a: any) => Number(a.id) === Number((filters as any).aulaId),
      );
      if (!ok) {
        setFilters((p) => ({ ...p, aulaId: undefined }) as any);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aulas]);

  const noDisponiblesEvents = useMemo(() => {
    if (!profesorFiltroId || !range.start || !range.end) return [] as any[];
    if (!['timeGridWeek', 'timeGridDay'].includes(view as any)) return [] as any[];

    const start = range.start!.startOf('day');
    const end = range.end!.endOf('day');
    const days: dayjs.Dayjs[] = [];
    let cursor = start;
    while (cursor.isBefore(end, 'day') || cursor.isSame(end, 'day')) {
      days.push(cursor);
      cursor = cursor.add(1, 'day');
    }

    const bg = blockedBg;
    const DIA_MAX = 24 * 60;

    const events: any[] = [];

    for (const d of days) {
      const dateStr = d.format('YYYY-MM-DD');
      if (diasNoDisponibles.has(dateStr)) continue;

      const espDelDiaTodos = especiales.filter((e) => e.fecha === dateStr);
      const espDelDia = espDelDiaTodos.filter(
        (e) => cumpleFormato(e.formatosMask) && cumpleCentro(e.idCentro),
      );

      // Separar especiales en positivos (ampliaciones) y bloqueos
      const espPositivos = espDelDia.filter((e) => !e.bloqueo);
      const espBloqueos = espDelDiaTodos.filter((e) => e.bloqueo);

      const dow = dayjsToModeloDow(d);

      // Filtrar por día de la semana
      const filasDelDia = rejillaRows.filter(
        (r: any) =>
          Number(r.diaSemana) === Number(dow) &&
          cumpleFormato(r.formatosMask) &&
          cumpleCentro(r.idCentro),
      );

      // VIGENCIAS EXCLUYENTES: Primero determinar si hay vigencias temporales activas
      // Si hay temporales, usar SOLO esas (ignorar base)
      // Si no hay temporales, usar base (sin fin)
      const temporalesActivas = filasDelDia.filter(
        (r: any) =>
          (r.vigenciaDesde ?? '1970-01-01') <= dateStr &&
          r.vigenciaHasta != null &&
          r.vigenciaHasta >= dateStr,
      );

      const filasAplicables =
        temporalesActivas.length > 0
          ? temporalesActivas // Si hay temporales, usar SOLO esas
          : filasDelDia.filter(
              (r: any) => (r.vigenciaDesde ?? '1970-01-01') <= dateStr && r.vigenciaHasta == null, // Si no hay temporales, usar SOLO base
            );

      // Disponibilidad semanal efectiva
      const weeklyDisponibles = filasAplicables.map((r: any) => ({
        ini: timeToMin(r.horaInicio),
        fin: timeToMin(r.horaFin),
      }));

      // Disponibilidad especial positiva
      const espPosDisponibles = espPositivos.map((e) => ({
        ini: timeToMin(e.horaInicio),
        fin: timeToMin(e.horaFin),
      }));

      const hayEspecialDia = espDelDiaTodos.length > 0;
      const disponibles = mergeIntervals(
        (hayEspecialDia ? espPosDisponibles : weeklyDisponibles).filter(
          (x) => Number.isFinite(x.ini) && Number.isFinite(x.fin) && x.fin > x.ini,
        ),
      );

      // huecos (no disponibles)
      const huecos: { ini: number; fin: number }[] = [];
      if (disponibles.length === 0) {
        huecos.push({ ini: 0, fin: DIA_MAX });
      } else {
        let prev = 0;
        for (const tr of disponibles) {
          if (tr.ini > prev) huecos.push({ ini: prev, fin: tr.ini });
          prev = Math.max(prev, tr.fin);
        }
        if (prev < DIA_MAX) huecos.push({ ini: prev, fin: DIA_MAX });
      }

      for (let i = 0; i < huecos.length; i++) {
        const h = huecos[i];
        const startIso = `${dateStr}T${minToHHMM(h.ini)}:00`;
        const endIso =
          h.fin >= DIA_MAX
            ? d.add(1, 'day').format('YYYY-MM-DD[T]00:00:00')
            : `${dateStr}T${minToHHMM(h.fin)}:00`;

        events.push({
          id: `ND-PARCIAL-${dateStr}-${i}`,
          start: startIso,
          end: endIso,
          display: 'background',
          backgroundColor: bg,
        });
      }
    }

    return events;
  }, [
    profesorFiltroId,
    range.start,
    range.end,
    view,
    blockedBg,
    diasNoDisponibles,
    especiales,
    rejillaRows,
    cumpleFormato,
    cumpleCentro,
  ]);

  const sinDisponibilidadMesEvents = useMemo(() => {
    if (!profesorFiltroId || !range.start || !range.end) return [] as any[];
    if ((view as any) !== 'dayGridMonth') return [] as any[];

    const start = range.start!.startOf('day');
    const last = range.end!.subtract(1, 'day').startOf('day');

    const events: any[] = [];
    let d = start;

    while (d.isBefore(last, 'day') || d.isSame(last, 'day')) {
      const dateStr = d.format('YYYY-MM-DD');

      if (!diasNoDisponibles.has(dateStr)) {
        const espDelDiaTodos = especiales.filter((e) => e.fecha === dateStr);
        const espDelDia = espDelDiaTodos.filter(
          (e) => cumpleFormato(e.formatosMask) && cumpleCentro(e.idCentro),
        );

        const dow = dayjsToModeloDow(d);

        // Filtrar por día de la semana
        const filasDelDia = rejillaRows.filter(
          (r: any) =>
            Number(r.diaSemana) === Number(dow) &&
            cumpleFormato(r.formatosMask) &&
            cumpleCentro(r.idCentro),
        );

        // VIGENCIAS EXCLUYENTES: Primero determinar si hay vigencias temporales activas
        // Si hay temporales, usar SOLO esas (ignorar base)
        // Si no hay temporales, usar base (sin fin)
        const temporalesActivas = filasDelDia.filter(
          (r: any) =>
            (r.vigenciaDesde ?? '1970-01-01') <= dateStr &&
            r.vigenciaHasta != null &&
            r.vigenciaHasta >= dateStr,
        );

        const filasAplicables =
          temporalesActivas.length > 0
            ? temporalesActivas // Si hay temporales, usar SOLO esas
            : filasDelDia.filter(
                (r: any) => (r.vigenciaDesde ?? '1970-01-01') <= dateStr && r.vigenciaHasta == null, // Si no hay temporales, usar SOLO base
              );

        // Separar especiales en positivos (ampliaciones) y bloqueos
        const espPositivos = espDelDia.filter((e) => !e.bloqueo);

        // Disponibilidad semanal efectiva
        const weeklyDisponibles = filasAplicables.map((r: any) => ({
          ini: timeToMin(r.horaInicio),
          fin: timeToMin(r.horaFin),
        }));

        // Disponibilidad especial positiva
        const espPosDisponibles = espPositivos.map((e) => ({
          ini: timeToMin(e.horaInicio),
          fin: timeToMin(e.horaFin),
        }));

        const hayEspecialDia = espDelDiaTodos.length > 0;
        const disponibles: { ini: number; fin: number }[] = mergeIntervals(
          (hayEspecialDia ? espPosDisponibles : weeklyDisponibles).filter(
            (x) => Number.isFinite(x.ini) && Number.isFinite(x.fin) && x.fin > x.ini,
          ),
        );

        // `disponibles` ya viene normalizado por mergeIntervals

        if (disponibles.length === 0) {
          events.push({
            id: `ND-SIN-DISP-${dateStr}`,
            start: dateStr,
            end: d.add(1, 'day').format('YYYY-MM-DD'),
            allDay: true,
            display: 'background',
            backgroundColor: blockedBg,
          });
        }
      }

      d = d.add(1, 'day');
    }

    return events;
  }, [
    view,
    profesorFiltroId,
    range.start,
    range.end,
    especiales,
    rejillaRows,
    diasNoDisponibles,
    blockedBg,
    cumpleFormato,
    cumpleCentro,
  ]);

  /* ✨ eventos combinados (si hay filtro por ID, ocultamos overlays) */
  const events = useMemo(
    () => [
      ...reservaEvents,
      ...(reservaIdFilter != null ? [] : bloqueoEvents),
      ...(reservaIdFilter != null ? [] : noDisponiblesEvents),
      ...(reservaIdFilter != null ? [] : sinDisponibilidadMesEvents),
    ],
    [
      reservaEvents,
      bloqueoEvents,
      noDisponiblesEvents,
      sinDisponibilidadMesEvents,
      reservaIdFilter,
    ],
  );

  /* Cuando cambian las fechas visibles */
  const handleDatesSet = (arg: DatesSetArg) => {
    const realEnd =
      (view as any) === 'dayGridMonth' ? dayjs(arg.end) : dayjs(arg.end).subtract(1, 'day');
    setRange({ start: dayjs(arg.start), end: realEnd });
    setView(arg.view.type as any);
  };

  const openCreateHandler = useCallback(
    (start: Date, end: Date) => canCreate && openCreate(start, end),
    [canCreate, openCreate],
  );

  const handleSelect = (arg: DateSelectArg) => canCreate && openCreateHandler(arg.start, arg.end);

  const handleDateClick = (arg: DateClickArg) => {
    if (!canCreate) return;
    if (arg.jsEvent.detail === 2) {
      const ini = dayjs(arg.date).minute(Math.floor(arg.date.getMinutes() / 5) * 5);
      openCreate(ini.toDate(), ini.add(1, 'hour').toDate());
    }
  };

  const handleEventClick = (arg: EventClickArg) => setSelected(arg.event.extendedProps);

  const handleEventDidMount = (info: EventMountArg) => {
    if (info.event.extendedProps?.incidencia) {
      info.el.style.border = `3px solid ${theme.palette.warning.main}`;
      info.el.style.borderRadius = '4px';
    }
  };

  /* ═══════ auxiliares UI ═══════ */
  const calRef = useRef<FullCalendar>(null);
  const nav = (fn: 'next' | 'prev') => calRef.current?.getApi()[fn]();
  const change = (v: string | null) => v && calRef.current?.getApi().changeView(v);

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const goto = (d: dayjs.Dayjs | null) => {
    if (d) calRef.current?.getApi().gotoDate(d.toDate());
    setAnchor(null);
  };

  const header = range.start && range.end ? headerTxt(view as any, range.start, range.end, t) : '';

  type LabelMode =
    | 'auto'
    | 'alumno'
    | 'asignatura'
    | 'alumnoAsignatura'
    | 'asignaturaAlumno'
    | 'profesor'
    | 'profesorAsignatura'
    | 'asignaturaProfesor';

  const [labelMode, setLabelMode] = useState<LabelMode>(() => {
    try {
      return (localStorage.getItem('calendar.labelMode') as LabelMode) || 'auto';
    } catch {
      return 'auto';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('calendar.labelMode', labelMode);
    } catch {}
  }, [labelMode]);

  const handleCloseDialog = () => {
    setDlg((p) => ({ ...p, open: false }));
    calRef.current?.getApi().refetchEvents();
  };

  const handleDownloadPdf = async () => {
    setAnchor(null);
    const node = captureRef.current;
    if (!node) return;

    const styleEl = document.createElement('style');
    styleEl.id = 'fc-print-fixes';
    styleEl.textContent = `
      #calendar-print-area .fc-scroller,
      #calendar-print-area .fc-scroller-harness,
      #calendar-print-area .fc-timegrid-body,
      #calendar-print-area .fc-daygrid-body,
      #calendar-print-area .fc-timegrid-body-natural {
        overflow: visible !important;
        max-height: none !important;
        height: auto !important;
      }
    `;
    document.head.appendChild(styleEl);

    const prevW = (node as HTMLElement).style.width;
    const prevH = (node as HTMLElement).style.height;
    (node as HTMLElement).style.width = `${(node as HTMLElement).scrollWidth}px`;
    (node as HTMLElement).style.height = 'auto';

    await new Promise((r) => setTimeout(r, 50));

    const canvas = await html2canvas(node as HTMLElement, {
      scale: Math.min(3, (window.devicePixelRatio || 1) * 2),
      useCORS: true,
      backgroundColor:
        (typeof window !== 'undefined' &&
          window.getComputedStyle(node as HTMLElement).backgroundColor) ||
        theme.palette.background.paper,
      windowWidth: (node as HTMLElement).scrollWidth,
      windowHeight: (node as HTMLElement).scrollHeight,
      scrollX: -window.scrollX,
      scrollY: -window.scrollY,
      logging: false,
    });

    (node as HTMLElement).style.width = prevW;
    (node as HTMLElement).style.height = prevH;
    document.getElementById('fc-print-fixes')?.remove();

    const pdf = new jsPDF({
      orientation: canvas.width >= canvas.height ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 8;

    const title = header || 'Calendario';
    const titleFontSize = 16;
    const pt2mm = 0.352777778;
    const lineFactor = 1.15;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(titleFontSize);
    pdf.setLineHeightFactor(lineFactor);

    const usableW = pageW - margin * 2;
    const titleLines = pdf.splitTextToSize(title, usableW);
    const titleBlockH = titleLines.length * (titleFontSize * pt2mm * lineFactor) + 2;

    const usableH = pageH - margin * 2;
    const contentHPerPage = Math.max(usableH - titleBlockH, 20);

    const ratio = Math.min(usableW / canvas.width, contentHPerPage / canvas.height);
    const sliceHpx = Math.floor(contentHPerPage / ratio);

    const partCanvas = document.createElement('canvas');
    const ctx = partCanvas.getContext('2d')!;
    partCanvas.width = canvas.width;

    let y = 0;
    let page = 0;
    while (y < canvas.height) {
      const h = Math.min(sliceHpx, canvas.height - y);
      partCanvas.height = h;

      ctx.clearRect(0, 0, partCanvas.width, partCanvas.height);
      ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);

      const partImg = partCanvas.toDataURL('image/png');

      if (page > 0) pdf.addPage();

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(titleFontSize);
      pdf.setLineHeightFactor(lineFactor);
      pdf.text(titleLines, margin, margin + titleFontSize * pt2mm);

      const imgW = canvas.width * ratio;
      const imgH = h * ratio;
      const x = margin + (usableW - imgW) / 2;
      const yImg = margin + titleBlockH;
      pdf.addImage(partImg, 'PNG', x, yImg, imgW, imgH);

      y += h;
      page++;
    }

    const startStr = range.start ? range.start.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
    const endStr = range.end ? range.end.format('YYYY-MM-DD') : startStr;

    try {
      pdf.setProperties({
        title: `Calendario ${title} (${startStr} a ${endStr})`,
      } as any);
    } catch {}

    pdf.save(`calendario_${view}_${startStr}_a_${endStr}.pdf`);
  };

  /* ✨ Tooltips de disponibilidad en vista MES (refuerzo) */
  useEffect(() => {
    if ((view as any) !== 'dayGridMonth' || !range.start || !range.end || !profesorFiltroId) return;

    const root: HTMLElement | null = captureRef.current as HTMLElement | null;
    if (!root) return;

    const cells = root.querySelectorAll('.fc-daygrid-day[data-date]');

    cells.forEach((cellEl) => {
      const td = cellEl as HTMLElement;
      const dateStr = td.getAttribute('data-date')!;
      const d = dayjs(dateStr);

      const espDelDiaTodos = especiales.filter((e) => e.fecha === dateStr);
      const espDelDia = espDelDiaTodos.filter(
        (e) => cumpleFormato(e.formatosMask) && cumpleCentro(e.idCentro),
      );

      // Separar especiales en positivos (ampliaciones) y bloqueos
      const espPositivos = espDelDia.filter((e) => !e.bloqueo);
      const espBloqueos = espDelDiaTodos.filter((e) => e.bloqueo);

      const dow = dayjsToModeloDow(d);

      // Filtrar por día de la semana
      const filasDelDia = rejillaRows.filter(
        (r: any) =>
          Number(r.diaSemana) === Number(dow) &&
          cumpleFormato(r.formatosMask) &&
          cumpleCentro(r.idCentro),
      );

      // VIGENCIAS EXCLUYENTES: Primero determinar si hay vigencias temporales activas
      // Si hay temporales, usar SOLO esas (ignorar base)
      // Si no hay temporales, usar base (sin fin)
      const temporalesActivas = filasDelDia.filter(
        (r: any) =>
          (r.vigenciaDesde ?? '1970-01-01') <= dateStr &&
          r.vigenciaHasta != null &&
          r.vigenciaHasta >= dateStr,
      );

      const filasAplicables =
        temporalesActivas.length > 0
          ? temporalesActivas // Si hay temporales, usar SOLO esas
          : filasDelDia.filter(
              (r: any) => (r.vigenciaDesde ?? '1970-01-01') <= dateStr && r.vigenciaHasta == null, // Si no hay temporales, usar SOLO base
            );

      // Disponibilidad semanal efectiva
      const weeklyDisponibles = filasAplicables.map((r: any) => ({
        ini: timeToMin(r.horaInicio),
        fin: timeToMin(r.horaFin),
      }));

      // Disponibilidad especial positiva
      const espPosDisponibles = espPositivos.map((e) => ({
        ini: timeToMin(e.horaInicio),
        fin: timeToMin(e.horaFin),
      }));

      const hayEspecialDia = espDelDiaTodos.length > 0;
      const disponibles = mergeIntervals(
        (hayEspecialDia ? espPosDisponibles : weeklyDisponibles).filter(
          (x) => Number.isFinite(x.ini) && Number.isFinite(x.fin) && x.fin > x.ini,
        ),
      );

      const tooltip = buildDisponibilidadTooltip({
        dateStr,
        disponibles,
        weeklyDisponibles,
        espPositivos,
        espBloqueos,
      });

      td.title = tooltip;
      const frame = td.querySelector('.fc-daygrid-day-frame') as HTMLElement | null;
      const top = td.querySelector('.fc-daygrid-day-top') as HTMLElement | null;
      const eventsWrap = td.querySelector('.fc-daygrid-day-events') as HTMLElement | null;
      const bg = td.querySelector('.fc-daygrid-day-bg') as HTMLElement | null;
      [frame, top, eventsWrap, bg].forEach((el) => el && el.setAttribute('title', tooltip));
    });
  }, [
    view,
    range.start,
    range.end,
    profesorFiltroId,
    especiales,
    rejillaRows,
    diasNoDisponibles,
    vacData,
    ausencias,
    cumpleFormato,
    cumpleCentro,
  ]);

  const inputSx = {
    flex: 1,
    width: '100%',
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': { borderColor: '#21a598 !important' },
      '&:hover fieldset': { borderColor: '#21a598 !important' },
    },
    '& .MuiInputLabel-root': {
      '&.Mui-focused': { color: '#21a598 !important' },
    },
    '& .MuiInputBase-input': { color: '#000 !important' },
    '& .MuiAutocomplete-endAdornment': { color: '#21a598 !important' },
  } as const;

  const listBoxSx = {
    fontSize: '0.875rem',
    maxHeight: 250,
    overflowY: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    '& .MuiAutocomplete-option': {
      '&[aria-selected="true"], &[aria-selected="true"].Mui-focusVisible, &[aria-selected="true"]:hover':
        { backgroundColor: '#fcf1ad !important', color: '#000 !important' },
      '&:not([aria-selected="true"]):hover, &:not([aria-selected="true"]).Mui-focusVisible': {
        backgroundColor: '#fcf1ad !important',
        color: '#000 !important',
      },
      '&:not([aria-selected="true"])': {
        backgroundColor: 'transparent',
        color: '#000',
      },
    },
  } as const;

  const renderEventContent = useCallback(
    (arg: EventContentArg) => {
      const { event } = arg;
      const viewType = arg.view?.type as string | undefined;

      if (event.display === 'background') return { html: '' };

      const { nombreAlumno, asignatura, nombreProf, esGrupal } = (event.extendedProps as any) ?? {};

      if (!nombreAlumno && !asignatura && !nombreProf) return { html: '' };

      const icon = esGrupal ? '👥' : '👤';

      // HH:mm de inicio/fin para todas las vistas
      const startTime = event.start ? dayjs(event.start).format('HH:mm') : '';
      const endTime = event.end ? dayjs(event.end).format('HH:mm') : '';
      const timeLabel = !event.allDay && (startTime || endTime) ? `${startTime}–${endTime}` : '';

      // === Vista Semana / Día: mostramos horas + 2 líneas ===
      if (viewType === 'timeGridWeek' || viewType === 'timeGridDay') {
        const line1 =
          labelMode === 'profesor' ||
          labelMode === 'profesorAsignatura' ||
          labelMode === 'asignaturaProfesor'
            ? (nombreProf ?? nombreAlumno ?? '')
            : (nombreAlumno ?? nombreProf ?? '');
        const line2 = asignatura ?? '';

        const tooltip = event.allDay
          ? `${line1} ${line2}`.trim()
          : `${timeLabel} ${line1} ${line2}`.trim();

        // Color de texto calculado para contraste (fallback por si el estilo del evento no aplica)
        const bgColor = (event.backgroundColor || '#21a598').trim();
        function getTextColor(bg: string) {
          const hex = bg.startsWith('#') ? bg.replace('#', '') : '21a598';
          const r = parseInt(hex.substring(0, 2), 16) || 33;
          const g = parseInt(hex.substring(2, 4), 16) || 165;
          const b = parseInt(hex.substring(4, 6), 16) || 152;
          const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
          return lum > 0.7 ? '#333333' : '#ffffff';
        }
        const textColor = getTextColor(bgColor);

        return {
          html: `
            <div style="
              display:flex;
              flex-direction:column;
              justify-content:flex-start;
              width:100%;
              height:100%;
              font-size:0.7rem;
              line-height:1rem;
              color:${textColor};
              overflow:hidden;
              padding:2px;
            " title="${tooltip}">
              ${
                timeLabel
                  ? `<div style="font-weight:600; margin-bottom:1px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${timeLabel}</div>`
                  : ''
              }
              <div style="white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${line1}</div>
              <div style="white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${line2}</div>
              <div style="margin-top:auto;align-self:flex-end;font-size:0.8rem;">${icon}</div>
            </div>
          `,
        };
      }

      const modeIncludesProf =
        labelMode === 'profesor' ||
        labelMode === 'profesorAsignatura' ||
        labelMode === 'asignaturaProfesor';

      const baseLabel = (() => {
        switch (labelMode) {
          case 'alumno':
            return nombreAlumno ?? asignatura ?? nombreProf ?? '';
          case 'asignatura':
            return asignatura ?? nombreAlumno ?? nombreProf ?? '';
          case 'alumnoAsignatura':
            return [nombreAlumno, asignatura].filter(Boolean).join(' · ');
          case 'asignaturaAlumno':
            return [asignatura, nombreAlumno].filter(Boolean).join(' · ');
          case 'profesor':
            return nombreProf ?? nombreAlumno ?? asignatura ?? '';
          case 'profesorAsignatura':
            return [nombreProf, asignatura].filter(Boolean).join(' · ');
          case 'asignaturaProfesor':
            return [asignatura, nombreProf].filter(Boolean).join(' · ');
          case 'auto':
          default:
            return (event.extendedProps as any)?.esGrupal
              ? (asignatura ?? nombreAlumno ?? nombreProf ?? '')
              : (nombreAlumno ?? asignatura ?? nombreProf ?? '');
        }
      })();

      let text = '';
      if (viewType === 'dayGridMonth') {
        if (event.allDay) {
          text = baseLabel;
        } else {
          text = `${startTime}-${endTime} ${baseLabel}`.trim();
        }
      } else {
        text =
          !modeIncludesProf && nombreProf
            ? [baseLabel, nombreProf].filter(Boolean).join(', ')
            : baseLabel;
        if (timeLabel) text = `${timeLabel} ${text}`.trim();
      }

      return {
        html: `
          <span style="display:flex;align-items:center;justify-content:space-between;position:relative;">
            <span style="flex-grow:1;text-overflow:ellipsis;overflow:hidden;">${text}</span>
            <span style="font-size:14px;position:absolute;right:5px;top:50%;transform:translateY(-50%);">
              ${icon}
            </span>
          </span>
        `,
      };
    },
    [labelMode],
  );

  /* ═══════ NUEVO: Items de LEYENDA de colores ═══════ */
  const filteredRowsForLegend = useMemo(
    () =>
      uniqueById(
        rows.filter((r) => {
          // ✨ Ocultar “pendiente de reagendar” para profesores (también de la leyenda)
          if (isProfesorLogueado && isPendienteReagendarEvt(r)) return false;

          const passId = reservaIdFilter != null ? Number((r as any).id) === reservaIdFilter : true;
          const passStatus =
            reservaIdFilter != null
              ? true // con ID ignoramos cancelado/activo
              : showCancelled
                ? isCancelledEvt(r)
                : !isCancelledEvt(r);
          return passId && passStatus;
        }),
      ),
    [rows, showCancelled, reservaIdFilter, isProfesorLogueado],
  );

  const legendItemsEstado: LegendItem[] = useMemo(() => {
    const map = new Map<string, { label: string; color: string; count: number }>();

    for (const r of filteredRowsForLegend) {
      const codeRaw = String((r as any).estadoCode ?? (r as any).estado ?? '').trim();
      if (!codeRaw) continue;
      const key = codeRaw.toLowerCase();
      const label = String((r as any).estado ?? codeRaw);
      const colorDb = normalizeColor((r as any).estadoColor);
      const colorFallback = getEstadoColor((r as any).estadoCode ?? (r as any).estado);
      const color = colorDb ?? colorFallback ?? '#999';

      const cur = map.get(key);
      if (cur) {
        cur.count += 1;
        if (colorDb && cur.color !== colorDb) cur.color = colorDb;
      } else {
        map.set(key, { label, color, count: 1 });
      }
    }

    const arr = Array.from(map.entries()).map(([key, v]) => ({
      key,
      label: v.label,
      color: v.color,
      count: v.count,
    }));

    return arr.sort((a, b) =>
      a.label.localeCompare(b.label, i18n.language, { sensitivity: 'base' }),
    );
  }, [filteredRowsForLegend, i18n.language]);

  const legendItemsProyecto: LegendItem[] = useMemo(() => {
    const map = new Map<number, { label: string; color: string; count: number }>();

    for (const r of filteredRowsForLegend) {
      const projId = Number((r as any).idProyecto);
      if (!Number.isFinite(projId)) continue;
      const proyecto = proyectosById.get(projId);
      const label = proyecto?.nombre ?? `Proyecto ${projId}`;

      const projColor = normalizeColor(proyecto?.calendarColor);
      const estadoDb = normalizeColor((r as any).estadoColor);
      const estadoFallback = getEstadoColor((r as any).estadoCode ?? (r as any).estado);
      const fallbackColor = estadoDb ?? estadoFallback ?? '#999';
      const color = projColor ?? fallbackColor;

      const cur = map.get(projId);
      if (cur) {
        cur.count += 1;
        if (projColor && cur.color !== projColor) cur.color = projColor;
      } else {
        map.set(projId, { label, color, count: 1 });
      }
    }

    const arr = Array.from(map.entries()).map(([key, v]) => ({
      key,
      label: v.label,
      color: v.color,
      count: v.count,
    }));

    return arr.sort((a, b) =>
      a.label.localeCompare(b.label, i18n.language, { sensitivity: 'base' }),
    );
  }, [filteredRowsForLegend, proyectosById, i18n.language]);

  const legendMode: 'proyecto' | 'estado' = isProfesorLogueado
    ? showCancelled
      ? 'estado'
      : 'proyecto'
    : colorMode;

  const legendItems = legendMode === 'proyecto' ? legendItemsProyecto : legendItemsEstado;

  /* ═══════════════════════════════════════════════ */

  /* ✨ Ir automáticamente a la fecha de la reserva buscada por ID */
  useEffect(() => {
    let cancelled = false;
    const goToReservaDate = async (id: number) => {
      // 1) Buscar en los rows del rango visible
      const hitLocal = rows.find((r) => Number((r as any).id) === Number(id));
      if (hitLocal) {
        setSearchHit(hitLocal);
        const dtStr =
          (hitLocal as any).inicio ||
          ((hitLocal as any).fecha && (hitLocal as any).horaInicio
            ? `${(hitLocal as any).fecha}T${(hitLocal as any).horaInicio}:00`
            : null);
        if (dtStr) calRef.current?.getApi().gotoDate(dayjs(dtStr).toDate());
        return;
      }

      // 2) Intento best-effort contra API directa (no rompe nada si no existe)
      try {
        const { data } = await api.get(`/reservas/${id}`);
        if (cancelled) return;
        if (data && Number(data.id) === Number(id)) {
          setSearchHit(data);
          const dtStr =
            data.inicio ||
            (data.fecha && data.horaInicio ? `${data.fecha}T${data.horaInicio}:00` : null);
          if (dtStr) calRef.current?.getApi().gotoDate(dayjs(dtStr).toDate());
        } else {
          setSearchHit(null);
        }
      } catch {
        if (!cancelled) setSearchHit(null);
      }
    };

    if (reservaIdFilter != null) goToReservaDate(reservaIdFilter);
    else setSearchHit(null);

    return () => {
      cancelled = true;
    };
  }, [reservaIdFilter, rows]);

  return (
    <>
      {/* ===== barra superior + filtros ===== */}
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton size="small" onClick={() => nav('prev')}>
            <ChevronLeft />
          </IconButton>
          <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
            <DateRange />
          </IconButton>
          <IconButton size="small" onClick={() => nav('next')}>
            <ChevronRight />
          </IconButton>

          <Typography variant="h6" sx={{ ml: 2, flexGrow: 1, textTransform: 'capitalize' }}>
            {header}
          </Typography>

          <ToggleButtonGroup
            size="small"
            value={view as any}
            exclusive
            onChange={(_, v) => change(v)}
            sx={{
              gap: 0.35,
              '& .MuiToggleButton-root': {
                height: 35,
                width: 64,
                color: '#888',
                border: '1px solid #b0b0b0',
                borderRadius: '4px !important',
                textTransform: 'none',
                fontWeight: 400,
                backgroundColor: 'transparent',
                boxSizing: 'border-box',
                transition: 'all 0.2s',
                '&.Mui-selected': {
                  backgroundColor: '#21a598 !important',
                  color: '#fff !important',
                  borderColor: '#21a598',
                },
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#21a598',
                  border: '2px solid #21a598',
                  outline: 'none',
                },
              },
            }}
          >
            <ToggleButton value="dayGridMonth">{t('calendar.view.month')}</ToggleButton>
            <ToggleButton value="timeGridWeek">{t('calendar.view.week')}</ToggleButton>
            <ToggleButton value="timeGridDay">{t('calendar.view.day')}</ToggleButton>
          </ToggleButtonGroup>

          {/* Selector de modo de color (oculto para profesores) */}
          {!isProfesorLogueado && (
            <TextField
              select
              size="small"
              variant="outlined"
              label={t('calendar.color.mode') ?? 'Color'}
              value={colorMode}
              onChange={(e) => setColorMode(e.target.value as ColorMode)}
              InputLabelProps={{ shrink: true }}
              sx={{
                ml: 1,
                minWidth: 200,
                '& .MuiOutlinedInput-root': {
                  fontWeight: 500,
                  borderRadius: '4px',
                  '& fieldset': { borderColor: '#b0b0b0', borderWidth: '1px' },
                  '&.Mui-focused fieldset': {
                    borderColor: '#21a598',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#888',
                  '&.Mui-focused': { color: '#21a598' },
                  fontWeight: 500,
                },
                '& .MuiSelect-select': { padding: '6px 12px', color: '#888' },
                '& .MuiSelect-icon': {
                  color: '#888',
                  transform: 'rotate(0deg) !important',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiSelect-icon': {
                  color: '#21a598',
                  transform: 'rotate(0deg) !important',
                },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        '&:hover': { backgroundColor: '#fcf1ad !important' },
                        '&.Mui-selected': {
                          backgroundColor: '#fff !important',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: '#fcf1ad !important',
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="estado">{t('calendar.color.byStatus') ?? 'Por estado'}</MenuItem>
              <MenuItem value="proyecto">
                {t('calendar.color.byProject') ?? 'Por proyecto'}
              </MenuItem>
            </TextField>
          )}

          {/* Select Label Mode */}
          <TextField
            select
            size="small"
            variant="outlined"
            label={t('calendar.label.mode') ?? 'Etiqueta'}
            value={labelMode}
            onChange={(e) => setLabelMode(e.target.value as any)}
            InputLabelProps={{ shrink: true }}
            sx={{
              ml: 1,
              minWidth: 240,
              '& .MuiOutlinedInput-root': {
                fontWeight: 500,
                borderRadius: '4px',
                '& fieldset': { borderColor: '#b0b0b0', borderWidth: '1px' },
                '&.Mui-focused fieldset': {
                  borderColor: '#21a598',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#888',
                '&.Mui-focused': { color: '#21a598' },
                fontWeight: 500,
              },
              '& .MuiSelect-select': { padding: '6px 12px', color: '#888' },
              '& .MuiSelect-icon': {
                color: '#888',
                transform: 'rotate(0deg) !important',
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiSelect-icon': {
                color: '#21a598',
                transform: 'rotate(0deg) !important',
              },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      '&:hover': { backgroundColor: '#fcf1ad !important' },
                      '&.Mui-selected': { backgroundColor: '#fff !important' },
                      '&.Mui-selected:hover': { backgroundColor: '#fcf1ad !important' },
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="auto">{t('calendar.label.auto') ?? 'Auto'}</MenuItem>
            <MenuItem value="alumno">{t('calendar.label.student') ?? 'Alumno'}</MenuItem>
            <MenuItem value="asignatura">{t('calendar.label.subject') ?? 'Asignatura'}</MenuItem>
            <MenuItem value="alumnoAsignatura">
              {t('calendar.label.studentFirst') ?? 'Alumno · Asignatura'}
            </MenuItem>
            <MenuItem value="asignaturaAlumno">
              {t('calendar.label.subjectFirst') ?? 'Asignatura · Alumno'}
            </MenuItem>
            <MenuItem value="profesor">{t('calendar.label.teacher') ?? 'Profesor'}</MenuItem>
            <MenuItem value="profesorAsignatura">
              {t('calendar.label.teacherSubject') ?? 'Profesor · Asignatura'}
            </MenuItem>
            <MenuItem value="asignaturaProfesor">
              {t('calendar.label.subjectTeacher') ?? 'Asignatura · Profesor'}
            </MenuItem>
          </TextField>

          <FormControlLabel
            sx={{ ml: 2 }}
            label={showCancelled ? t('calendar.status.cancelled') : t('calendar.status.active')}
            control={
              <Switch
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#21a598 !important',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#21a598 !important',
                  },
                  '& .MuiSwitch-switchBase': {
                    color: '#bdc5c6 !important',
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#e2e2e2 !important',
                  },
                }}
              />
            }
          />

          <FormControlLabel
            sx={{ ml: 2 }}
            label={
              showInactiveUsers ? t('calendar.users.showInactive') : t('calendar.users.showActive')
            }
            control={
              <Switch
                checked={showInactiveUsers}
                onChange={(e) => onShowInactiveUsersChange?.(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#21a598 !important',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#21a598 !important',
                  },
                  '& .MuiSwitch-switchBase': {
                    color: '#bdc5c6 !important',
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#e2e2e2 !important',
                  },
                }}
              />
            }
          />

          <IconButton size="small" onClick={handleDownloadPdf} title="Descargar PDF del calendario">
            <PictureAsPdf />
          </IconButton>
        </Stack>

        {/* filtros SIEMPRE visibles */}
        <Box sx={{ p: 2, borderRadius: 1 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {/* ✨ NUEVO: Buscador por ID */}
            <TextField
              size="small"
              sx={{ ...inputSx, width: 160 }}
              label={t('calendar.filter.reservationId') ?? 'ID reserva'}
              placeholder={t('calendar.filter.reservationId') ?? 'ID reserva'}
              inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
              value={reservaIdQuery}
              onChange={(e) => setReservaIdQuery(e.target.value.replace(/[^\d]/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyIdFilter();
              }}
            />
            <Button
              onClick={applyIdFilter}
              sx={{
                backgroundColor: '#21a598',
                color: '#fff',
                textTransform: 'uppercase',
                fontWeight: 400,
                boxShadow: 'none',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#1a8578',
                  boxShadow: 'none',
                },
              }}
              title={t('calendar.filter.applyId', 'Filtrar ID') as string}
            >
              {t('calendar.filter.applyId', 'Filtrar ID')}
            </Button>
            {reservaIdFilter != null && (
              <Button
                onClick={clearIdFilter}
                sx={{
                  backgroundColor: '#21a598',
                  color: '#fff',
                  textTransform: 'uppercase',
                  fontWeight: 400,
                  boxShadow: 'none',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#1a8578',
                    boxShadow: 'none',
                  },
                }}
                title={t('calendar.filter.clearId', 'Quitar ID') as string}
              >
                {t('calendar.filter.clearId', 'Quitar ID')}
              </Button>
            )}

            {/* Profesor */}
            {/* Profesor */}
            <Autocomplete
              size="small"
              sx={{ ...inputSx, width: 160 }}
              ListboxProps={{ sx: listBoxSx }}
              options={profesores}
              getOptionLabel={(o) => o.nombreCompleto ?? String(o.id)}
              value={profesores.find((o) => Number(o.id) === Number(filters.profesorId)) ?? null}
              onChange={(_, v) => setFilter('profesorId', v?.id)}
              renderInput={(p) => <TextField {...p} label={t('calendar.filter.professor')} />}
              disabled={!!fixedFilters.profesorId}
            />

            {/* Cliente — filtrado por profesor si aplica */}
            <Autocomplete
              size="small"
              sx={{ ...inputSx, width: 160 }}
              ListboxProps={{ sx: listBoxSx }}
              options={clientesDelProfesor}
              getOptionLabel={(o) => o.nombreCompleto ?? String(o.id)}
              value={
                clientesDelProfesor.find((o) => Number(o.id) === Number(filters.clienteIdUsr)) ??
                null
              }
              onChange={(_, v) => setFilter('clienteIdUsr', v?.id)}
              renderInput={(p) => <TextField {...p} label={t('calendar.filter.client')} />}
              disabled={!!fixedFilters.clienteIdUsr}
            />

            {/* Asignatura */}
            <Autocomplete
              size="small"
              sx={{ ...inputSx, width: 160 }}
              ListboxProps={{ sx: listBoxSx }}
              options={asignaturas}
              getOptionLabel={(o) => o.nombre}
              value={asignaturas.find((o) => Number(o.id) === Number(filters.asignaturaId)) ?? null}
              onChange={(_, v) => setFilter('asignaturaId', v?.id)}
              renderInput={(p) => <TextField {...p} label={t('calendar.filter.subject')} />}
            />

            {/* Formato */}
            <Autocomplete
              size="small"
              sx={{ ...inputSx, width: 160 }}
              ListboxProps={{ sx: listBoxSx }}
              options={formatos}
              getOptionLabel={(o) => o.nombreFormato}
              value={
                formatos.find(
                  (o) =>
                    Number(o.id) === Number((fixedFilters as any).idFormato ?? filters.idFormato),
                ) ?? null
              }
              onChange={(_, v) => setFilter('idFormato', v?.id)}
              renderInput={(p) => <TextField {...p} label={t('calendar.filter.format')} />}
              disabled={!!(fixedFilters as any).idFormato}
            />

            {/* Centro */}
            <Autocomplete
              size="small"
              sx={{ ...inputSx, width: 160 }}
              ListboxProps={{ sx: listBoxSx }}
              options={centros}
              getOptionLabel={(o) => o.nombre}
              value={centros.find((o) => Number(o.id) === Number(filters.centroId)) ?? null}
              onChange={(_, v) => setFilter('centroId', v?.id)}
              renderInput={(p) => <TextField {...p} label={t('calendar.filter.center')} />}
            />

            {/* ✨ NUEVO (2.3): Aula — SOLO STAFF y dependiente del centro */}
            {isStaff && (
              <Autocomplete
                size="small"
                sx={{ ...inputSx, width: 200 }}
                ListboxProps={{ sx: listBoxSx }}
                options={aulas as any[]}
                getOptionLabel={(o: any) => o?.nombre ?? String(o?.id ?? '')}
                isOptionEqualToValue={(o: any, v: any) => Number(o.id) === Number(v.id)}
                value={
                  (aulas as any[]).find(
                    (a: any) => Number(a.id) === Number((filters as any).aulaId),
                  ) ?? null
                }
                onChange={(_, v: any) => setFilters((p) => ({ ...p, aulaId: v?.id }) as any)}
                renderInput={(p) => (
                  <TextField {...p} label={t('calendar.filter.classroom') ?? 'Aula'} />
                )}
                disabled={!centroFiltroId}
              />
            )}

            {/* Estado de aula: independiente del centro para localizar pendientes */}
            {isStaff && (
              <Autocomplete
                size="small"
                sx={{ ...inputSx, width: 190 }}
                ListboxProps={{ sx: listBoxSx }}
                options={AULA_ESTADOS as readonly string[]}
                value={(filters as any).aulaEstado ?? null}
                onChange={(_, v) =>
                  setFilters((p) => ({ ...p, aulaEstado: v ?? undefined }) as any)
                }
                renderInput={(p) => (
                  <TextField {...p} label={t('calendar.filter.classroomStatus') ?? 'Estado aula'} />
                )}
              />
            )}

            {/* Curso */}
            <Autocomplete
              size="small"
              sx={{ ...inputSx, width: 160 }}
              ListboxProps={{ sx: listBoxSx }}
              options={CURSOS as readonly string[]}
              value={filters.curso ?? null}
              onChange={(_, v) => setFilter('curso', v ?? undefined)}
              renderInput={(p) => <TextField {...p} label={t('calendar.filter.course')} />}
              disableCloseOnSelect
            />

            {/* Estado */}
            <Autocomplete
              size="small"
              sx={{ ...inputSx, width: 160 }}
              ListboxProps={{ sx: listBoxSx }}
              options={ESTADOS as readonly string[]}
              value={filters.estado ?? null}
              onChange={(_, v) => setFilter('estado', v ?? undefined)}
              renderInput={(params) => (
                <TextField {...params} label={t('calendar.filter.status')} />
              )}
              disableCloseOnSelect={false}
            />

            {/* Proyecto */}
            <Autocomplete
              size="small"
              sx={{ ...inputSx, width: 210 }}
              ListboxProps={{ sx: listBoxSx }}
              options={proyectos}
              getOptionLabel={(o) => o.nombre}
              value={proyectos.find((o) => Number(o.id) === Number(filters.idProyecto)) ?? null}
              onChange={(_, v) => setFilter('idProyecto', v?.id)}
              renderInput={(p) => <TextField {...p} label={t('calendar.filter.project')} />}
            />

            <Button
              onClick={clearFilters}
              sx={{
                backgroundColor: '#21a598',
                color: '#fff',
                textTransform: 'uppercase',
                fontWeight: 400,
                boxShadow: 'none',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#1a8578',
                  boxShadow: 'none',
                },
              }}
            >
              {t('calendar.filter.clear')}
            </Button>
          </Stack>
        </Box>

        {/* ═══════ NUEVO: LEYENDA DE COLORES ═══════ */}
        <Box sx={{ px: 2, pb: 1 }}>
          <CalendarLegend
            mode={legendMode}
            items={legendItems}
            dense
            title={
              legendMode === 'proyecto'
                ? t('calendar.legend.projects', 'Proyectos')
                : t('calendar.legend.statuses', 'Estados')
            }
          />
        </Box>
      </Stack>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
          <DateCalendar
            value={null}
            onChange={goto}
            showDaysOutsideCurrentMonth
            fixedWeekNumber={6}
          />
        </LocalizationProvider>
      </Popover>

      {loading && <CircularProgress size={24} sx={{ position: 'absolute', top: 8, right: 8 }} />}

      <style>{`
  /* Contenedor principal */
  #calendar-print-area {
    border: 2px solid #000;
  }

  /* Bordes internos Mes (dayGrid) */
  .fc-daygrid-day, 
  .fc-col-header-cell {
    border: 1px solid #000 !important;
  }

  /* Cabeceras de mes */
  .fc-col-header-cell {
    background-color: #000 !important;
    color: #fff !important;
    font-weight: bold;
    text-transform: uppercase;
  }

  /* ------------------ Semana/Día (timeGrid) ------------------ */
  /* Columna de horas (eje vertical) + etiquetas + cabeceras de día */
  .fc-timegrid .fc-timegrid-axis-cushion,
  .fc-timegrid .fc-timegrid-slot-label,
  .fc-col-header-cell-cushion {
    color: #fff !important;
    background-color: #000 !important;
  }

  /* Etiquetas de horas tamaño */
  .fc-timegrid .fc-timegrid-slot-label {
    font-size: 0.7rem !important;
  }

  /* Separadores internos */
  .fc-timegrid-slot,
  .fc-timegrid-slot-minor {
    border-bottom: 1px solid #939393ff !important;
  }

  /* Separadores verticales (columnas de días) */
  .fc-timegrid-col {
    border-right: 1px solid #939393ff !important;
  }

  /* Texto de eventos más pequeño */
  .fc .fc-event .fc-event-title {
    font-size: 0.65rem !important;
    line-height: 1rem !important;
  }

  /* Vista semana: ajustar tamaño general del evento */
  .fc-timegrid-week .fc-event,
  .fc-timegrid-week .fc-event .fc-event-title {
    font-size: 0.65rem !important;
  }
      `}</style>

      {/* calendario FullCalendar */}
      <div
        id="calendar-print-area"
        ref={captureRef}
        style={{
          background: theme.palette.background.paper,
          overflow: 'visible',
        }}
      >
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          locale={i18n.language}
          firstDay={1}
          height="auto"
          headerToolbar={false}
          nowIndicator
          events={events}
          selectable={canCreate}
          selectMirror={canCreate}
          select={handleSelect}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          eventDisplay="block"
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }}
          eventContent={renderEventContent}
          eventDidMount={handleEventDidMount}
          eventOverlap={false}
          slotEventOverlap={false}
          allDaySlot={false}
          slotDuration="00:30:00"
          dayCellDidMount={(arg: any) => {
            if ((view as any) !== 'dayGridMonth' || !profesorFiltroId) return;

            const date = dayjs(arg.date);
            const dateStr = date.format('YYYY-MM-DD');

            const espDelDia = especiales.filter((e) => e.fecha === dateStr);

            const espDelDiaFiltrado = espDelDia.filter(
              (e) => cumpleFormato(e.formatosMask) && cumpleCentro(e.idCentro),
            );

            // Separar especiales en positivos (ampliaciones) y bloqueos
            const espPositivos = espDelDiaFiltrado.filter((e) => !e.bloqueo);
            const espBloqueos = espDelDia.filter((e) => e.bloqueo);

            const dow = dayjsToModeloDow(date);

            // Filtrar por día de la semana
            const filasDelDia = rejillaRows.filter(
              (r: any) =>
                Number(r.diaSemana) === Number(dow) &&
                cumpleFormato(r.formatosMask) &&
                cumpleCentro(r.idCentro),
            );

            // VIGENCIAS EXCLUYENTES: Primero determinar si hay vigencias temporales activas
            // Si hay temporales, usar SOLO esas (ignorar base)
            // Si no hay temporales, usar base (sin fin)
            const temporalesActivas = filasDelDia.filter(
              (r: any) =>
                (r.vigenciaDesde ?? '1970-01-01') <= dateStr &&
                r.vigenciaHasta != null &&
                r.vigenciaHasta >= dateStr,
            );

            const filasAplicables =
              temporalesActivas.length > 0
                ? temporalesActivas // Si hay temporales, usar SOLO esas
                : filasDelDia.filter(
                    (r: any) =>
                      (r.vigenciaDesde ?? '1970-01-01') <= dateStr && r.vigenciaHasta == null, // Si no hay temporales, usar SOLO base
                  );

            // Disponibilidad semanal efectiva
            const weeklyDisponibles = filasAplicables.map((r: any) => ({
              ini: timeToMin(r.horaInicio),
              fin: timeToMin(r.horaFin),
            }));

            // Disponibilidad especial positiva
            const espPosDisponibles = espPositivos.map((e) => ({
              ini: timeToMin(e.horaInicio),
              fin: timeToMin(e.horaFin),
            }));

            const hayEspecialDia = espDelDia.length > 0;
            const disponibles = mergeIntervals(
              (hayEspecialDia ? espPosDisponibles : weeklyDisponibles).filter(
                (x) => Number.isFinite(x.ini) && Number.isFinite(x.fin) && x.fin > x.ini,
              ),
            );

            const tooltip = buildDisponibilidadTooltip({
              dateStr,
              disponibles,
              weeklyDisponibles,
              espPositivos,
              espBloqueos,
            });

            arg.el.title = tooltip;
            const frame = arg.el.querySelector('.fc-daygrid-day-frame') as HTMLElement | null;
            const top = arg.el.querySelector('.fc-daygrid-day-top') as HTMLElement | null;
            const eventsWrap = arg.el.querySelector('.fc-daygrid-day-events') as HTMLElement | null;
            const bg = arg.el.querySelector('.fc-daygrid-day-bg') as HTMLElement | null;
            [frame, top, eventsWrap, bg].forEach((el) => el && el.setAttribute('title', tooltip));
          }}
        />
      </div>

      {/* FAB “Nueva reserva” */}
      {canCreate && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 24, right: 24 }}
          onClick={() => openCreate(new Date(), dayjs().add(1, 'hour').toDate())}
        >
          <AddIcon />
        </Fab>
      )}

      {/* diálogo lectura */}
      {selected && (
        <ReservaDialog
          open
          reserva={selected}
          onClose={() => {
            setSelected(null);
            refetchEvents();
          }}
          onEdit={canEdit ? openEdit : undefined}
          onDuplicate={canCreate ? openDuplicate : undefined}
          onAmpliar={canCreate ? goToNuevaReserva : undefined}
        />
      )}

      {/* diálogo create / edit */}
      <ReservationFormDialog
        open={dlg.open}
        mode={dlg.mode}
        initialValues={dlg.initial}
        reservaId={dlg.id}
        onClose={handleCloseDialog}
      />
    </>
  );
}
